import {isValidationFailedResponse, ValidationFailures} from '@/api';
import {PasswordResponse, Providers} from '@/api/models/Providers.ts';
import ValidatedFormGroup from '@/components/ValidatedFormGroup.tsx';
import {Toaster, ToastErrors} from '@/toaster.ts';
import {Button, Classes, Dialog, InputGroup} from '@blueprintjs/core';
import {ChangeEventHandler, EventHandler, ReactElement, SyntheticEvent, useCallback, useState} from 'react';

export type SavedHandlerFn = (response: PasswordResponse) => void;

interface Props {
	onClose: () => void,
	onSaved: SavedHandlerFn,
}

export default function CreateProviderDialog({onClose, onSaved}: Props): ReactElement {
	const [validationFailures, setValidationFailures] = useState<ValidationFailures | null>(null);
	const [saving, setSaving] = useState(false);

	const [username, setUsername] = useState('');
	const onUsernameChange: ChangeEventHandler<HTMLInputElement> = useCallback(event => {
		setUsername(event.currentTarget.value);
	}, []);

	const onSubmit: EventHandler<SyntheticEvent> = useCallback(async event => {
		event.preventDefault();

		if (saving)
			return;

		setSaving(true);

		let response: PasswordResponse;

		try {
			response = await Providers.create({
				username,
			});
		} catch (e) {
			if (isValidationFailedResponse(e)) {
				Toaster.error(e.error.message);
				setValidationFailures(e.error.context.failures);
			} else
				ToastErrors.fromError(e);

			return;
		} finally {
			setSaving(false);
		}

		onSaved(response);
	}, [onSaved, saving, username]);

	return (
		<Dialog isOpen={true} onClose={onClose} title="Add Provider">
			<form onSubmit={onSubmit}>
				<div className={Classes.DIALOG_BODY}>
					<ValidatedFormGroup label="Username" labelFor="username" failures={validationFailures}>
						<InputGroup value={username} onChange={onUsernameChange} />
					</ValidatedFormGroup>
				</div>

				<div className={Classes.DIALOG_FOOTER}>
					<div className={Classes.DIALOG_FOOTER_ACTIONS}>
						<Button text="Cancel" onClick={onClose} disabled={saving} />
						<Button type="submit" intent="primary" text="Submit" onClick={onSubmit} loading={saving} />
					</div>
				</div>
			</form>
		</Dialog>
	);
}
